import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Top-right admin chrome button — fetches /api/backup/export which streams a ZIP
 * with one JSON file per course (incl. snapshots), plus `learners.json`,
 * `progress.json` and a `manifest.json`.
 */
export default function BackupButton() {
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    const t = toast.loading("Preparing backup…");
    try {
      // Use the shared axios instance so auth header & baseURL are wired correctly,
      // and ask for a binary blob (not auto-JSON-parsed) so the ZIP isn't corrupted.
      const res = await api.get("/backup/export", { responseType: "blob" });
      const blob = res.data;

      // Pull filename out of Content-Disposition, fall back to today's date
      const dispo = res.headers?.["content-disposition"] || res.headers?.get?.("content-disposition") || "";
      const match = /filename="?([^"]+)"?/.exec(dispo);
      const filename = match ? match[1] : `univicoustic-backup-${new Date().toISOString().slice(0, 10)}.zip`;

      // Trigger a real browser download
      const a = document.createElement("a");
      const objUrl = URL.createObjectURL(blob);
      a.href = objUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Defer revoke so Safari has time to start the download
      setTimeout(() => URL.revokeObjectURL(objUrl), 1000);

      toast.dismiss(t);
      toast.success("Backup downloaded", {
        description: `${filename} · ${(blob.size / (1024 * 1024)).toFixed(1)} MB`,
        icon: <ShieldCheck size={14} className="text-[#E53935]"/>,
      });
    } catch (e) {
      toast.dismiss(t);
      const msg = e?.response?.status
        ? `HTTP ${e.response.status} — ${e.response.statusText || ""}`
        : (e?.message || "Unknown error");
      toast.error("Backup failed", { description: msg.slice(0, 200) });
      // eslint-disable-next-line no-console
      console.error("[BackupButton] download failed:", e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      onClick={download}
      disabled={busy}
      variant="outline"
      size="sm"
      className="h-8 text-xs gap-1.5 border-slate-200 hover:border-[#E53935] hover:text-[#E53935]"
      data-testid="download-backup-button"
      title="Download a ZIP backup of every course, module, snapshot, learner and progress record"
    >
      {busy ? <Loader2 size={13} className="animate-spin"/> : <Download size={13}/>}
      {busy ? "Bundling…" : "Backup"}
    </Button>
  );
}
